/**
 * AuthUI — Manages the HTML overlay dialogs for authentication.
 *
 * Google Sign-In requires real DOM elements (not canvas), so we use
 * an HTML overlay that floats above the Phaser canvas. This module
 * handles showing/hiding the overlay and rendering the appropriate
 * dialog (sign-in, display name prompt, profile/logout, sync conflict).
 */

import { AuthService, AuthUser } from './AuthService';
import type { SyncConflictActions } from './SaveSyncService';

class _AuthUI {
  private _overlay: HTMLElement | null = null;
  private _dialog: HTMLElement | null = null;
  private _onClose: (() => void) | null = null;

  private get overlay(): HTMLElement {
    if (!this._overlay) {
      this._overlay = document.getElementById('auth-overlay');
    }
    return this._overlay!;
  }

  private get dialog(): HTMLElement {
    if (!this._dialog) {
      this._dialog = document.getElementById('auth-dialog');
    }
    return this._dialog!;
  }

  /** Show the sign-in dialog (Google button + close) */
  showSignIn(onComplete?: () => void): void {
    this._onClose = onComplete || null;

    this.dialog.innerHTML = `
      <h2>Login</h2>
      <p>Sign in with Google to save your progress online</p>
      <div class="google-btn-container" id="google-signin-btn"></div>
      <div class="auth-error" id="auth-signin-error"></div>
      <button class="auth-close-btn" id="auth-close">Cancel</button>
    `;

    this._show();

    // Close button
    document.getElementById('auth-close')?.addEventListener('click', () => this._hide());

    // Backdrop close
    this.overlay.querySelector('.auth-backdrop')?.addEventListener('click', () => this._hide());

    // Render Google button — must be deferred so the container is visible and painted
    // (Google's renderButton measures the container and fails silently if it has zero size)
    requestAnimationFrame(() => {
      requestAnimationFrame(async () => {
        const btnContainer = document.getElementById('google-signin-btn');
        if (btnContainer) {
          await AuthService.renderGoogleButton(btnContainer);
        }
      });
    });

    // Listen for loading state — show spinner while waiting for server
    const unsubLoading = AuthService.onLoading((loading) => {
      if (loading) {
        this.dialog.innerHTML = `
          <div class="auth-loading">
            <div class="auth-spinner"></div>
            <h2>Logging in...</h2>
            <p>Verifying your account</p>
          </div>
        `;
      }
    });

    // Listen for auth errors — show them in the dialog (revert from loading state)
    const unsubErr = AuthService.onError((msg) => {
      // If we're in loading state, revert back to sign-in view with the error
      this.dialog.innerHTML = `
        <h2>Login</h2>
        <p>Sign in with Google to save your progress online</p>
        <div class="google-btn-container" id="google-signin-btn"></div>
        <div class="auth-error" id="auth-signin-error">${msg}</div>
        <button class="auth-close-btn" id="auth-close">Cancel</button>
      `;
      document.getElementById('auth-close')?.addEventListener('click', () => this._hide());
      requestAnimationFrame(() => {
        requestAnimationFrame(async () => {
          const btnContainer = document.getElementById('google-signin-btn');
          if (btnContainer) await AuthService.renderGoogleButton(btnContainer);
        });
      });
    });

    // Listen for auth changes — if user signs in, show display name prompt or close
    const unsub = AuthService.onChange((user) => {
      if (user) {
        unsub();
        unsubErr();
        unsubLoading();
        if (AuthService.needsDisplayName) {
          this.showDisplayNamePrompt(onComplete);
        } else {
          this._hide();
        }
      }
    });
  }

  /** Show the display name prompt after first sign-in */
  showDisplayNamePrompt(onComplete?: () => void): void {
    this._onClose = onComplete || null;

    this.dialog.innerHTML = `
      <h2>Choose a Display Name</h2>
      <p>This is how other players will see you</p>
      <input type="text" id="display-name-input" placeholder="Enter display name" maxlength="30" autocomplete="off" />
      <div class="auth-error" id="display-name-error"></div>
      <button class="auth-submit-btn" id="display-name-submit">Save</button>
    `;

    this._show();

    const input = document.getElementById('display-name-input') as HTMLInputElement;
    const submitBtn = document.getElementById('display-name-submit') as HTMLButtonElement;
    const errorEl = document.getElementById('display-name-error')!;

    // Focus input
    setTimeout(() => input?.focus(), 100);

    const submit = async () => {
      const name = input.value.trim();
      if (name.length < 2) {
        errorEl.textContent = 'Name must be at least 2 characters';
        return;
      }
      if (name.length > 30) {
        errorEl.textContent = 'Name must be 30 characters or less';
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = 'Saving...';
      errorEl.textContent = '';

      const ok = await AuthService.setDisplayName(name);
      if (ok) {
        this._hide();
      } else {
        errorEl.textContent = 'Failed to save. Try again.';
        submitBtn.disabled = false;
        submitBtn.textContent = 'Save';
      }
    };

    submitBtn.addEventListener('click', submit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') submit();
    });

    // Don't close on backdrop for display name (they need to choose one)
  }

  /** Show the profile dialog (logged in user info + logout) */
  showProfile(onComplete?: () => void): void {
    const user = AuthService.user;
    if (!user) return;

    this._onClose = onComplete || null;

    const pictureHtml = user.picture_url
      ? `<img src="${user.picture_url}" alt="Profile" referrerpolicy="no-referrer" />`
      : `<div style="width:64px;height:64px;border-radius:50%;background:#3d3836;border:2px solid #d8a43a;display:flex;align-items:center;justify-content:center;font-size:28px;color:#d8a43a;">${(user.display_name || '?')[0].toUpperCase()}</div>`;

    this.dialog.innerHTML = `
      <div class="profile-section">
        ${pictureHtml}
        <div class="profile-name">${user.display_name || 'No display name'}</div>
        <div class="profile-email">${user.email}</div>
        <button class="auth-logout-btn" id="auth-logout">Log Out</button>
      </div>
      <br/>
      <button class="auth-close-btn" id="auth-close">Close</button>
    `;

    this._show();

    document.getElementById('auth-logout')?.addEventListener('click', async () => {
      await AuthService.logout();
      this._hide();
    });

    document.getElementById('auth-close')?.addEventListener('click', () => this._hide());
    this.overlay.querySelector('.auth-backdrop')?.addEventListener('click', () => this._hide());
  }

  /**
   * Show the sync-conflict dialog when both local and account have saved progress.
   * User can: overwrite account with local, use account progress (replace local), or cancel login.
   */
  showSyncConflictDialog(actions: SyncConflictActions): void {
    this.dialog.innerHTML = `
      <h2>Save data conflict</h2>
      <p>This device has saved progress, and your account also has saved progress. Choose how to continue.</p>
      <div class="auth-conflict-buttons">
        <button class="auth-conflict-btn" id="sync-overwrite-local">Use this device's progress</button>
        <p class="auth-conflict-hint">Replace your account's saves with this device's progress</p>
        <button class="auth-conflict-btn" id="sync-use-account">Use my account's progress</button>
        <p class="auth-conflict-hint">Replace this device's progress with your account's saves</p>
        <button class="auth-cancel-login-btn" id="sync-cancel-login">Don't log in</button>
        <p class="auth-conflict-hint">Stay offline and keep this device's progress only</p>
      </div>
    `;

    this._show();

    const run = async (fn: () => Promise<void>) => {
      try {
        await fn();
        this._hide();
      } catch (e) {
        console.warn('[AuthUI] Sync action failed:', e);
      }
    };

    document.getElementById('sync-overwrite-local')?.addEventListener('click', () => run(actions.overwriteWithLocal));
    document.getElementById('sync-use-account')?.addEventListener('click', () => run(actions.useAccount));
    document.getElementById('sync-cancel-login')?.addEventListener('click', () => run(actions.cancelLogin));
    this.overlay.querySelector('.auth-backdrop')?.addEventListener('click', () => {});
  }

  /** Is the overlay currently visible? */
  get isVisible(): boolean {
    return this.overlay?.classList.contains('visible') ?? false;
  }

  // ─── Private ─────────────────────────────────────────────────────

  private _show(): void {
    this.overlay.classList.add('visible');
  }

  private _hide(): void {
    this.overlay.classList.remove('visible');
    this.dialog.innerHTML = '';
    if (this._onClose) {
      const cb = this._onClose;
      this._onClose = null;
      cb();
    }
  }
}

export const AuthUI = new _AuthUI();
